import { Currency } from '@principle-theorem/accounting';
import {
  WithRef,
  type ArchivedDocument,
  type DocumentReference,
  type INamedDocument,
  type ISoftDelete,
  type Timestamp,
} from '@principle-theorem/shared';
import { Moment } from 'moment-timezone';
import { type IAppointment } from '../appointment/appointment';
import { type IInvoice } from '../invoice/invoice';
import { IPractice } from '../practice/practice';
import { type IStaffer } from '../staffer/staffer';
import { IUsedAccountCredit } from '../transaction/account-credit-transaction-extended-data';
import { ITransaction } from '../transaction/transaction';
import { ITreatmentCategory } from '../treatment-category';

export enum AccountCreditCollection {
  AccountCreditHistory = 'accountCreditHistory',
}

export enum AccountCreditType {
  Deposit = 'deposit',
  Refund = 'refund',
  Overpayment = 'overpayment',
  /**
    @deprecated
  */
  Manual = 'manual',
}

export interface IAccountCredit extends ISoftDelete {
  description: string;
  amount: number;
  currency?: Currency;
  type: AccountCreditType;
  reference?: string;
  invoice?: DocumentReference<IInvoice>;
  practiceRef?: DocumentReference<IPractice>;
  depositUid?: string;
  reservedFor: {
    practitioner?: INamedDocument<IStaffer>;
    treatment?: INamedDocument[];
    treatmentCategory?: DocumentReference<ITreatmentCategory>;
    appointmentRef?: DocumentReference<IAppointment>;
  };
  used: number;
  issued?: Timestamp;
  amendmentOf?: DocumentReference<ArchivedDocument<IAccountCredit>>;
}

export interface IAccountCreditAttributed {
  accountCredit: WithRef<IAccountCredit>;
  amount: number;
}

/**
 * Splits a patient's account credits by how they relate to one appointment, so
 * checkout can spend the credits reserved for it before falling back to
 * unreserved balance, and warn before touching credit held for another booking.
 */
export interface IAppointmentCreditClassification {
  matching: WithRef<IAccountCredit>[];
  unreserved: WithRef<IAccountCredit>[];
  otherAppointment: WithRef<IAccountCredit>[];
}

export interface IPayWithCreditResults {
  remaining: number;
  alteredCredits: IAccountCreditAttributed[];
}

export interface IAccountCreditRefunded {
  accountCreditRef: DocumentReference<IAccountCredit>;
  amount: number;
  refundedAt?: Timestamp;
}

export interface IRefundCreditResults {
  remaining: number;
  alteredCredits: IAccountCreditRefunded[];
}

export interface ISplitAccountCreditFormData {
  description: string;
  amount: number;
  createdAt: Moment;
  practitioner?: INamedDocument<IStaffer>;
  treatmentCategory?: DocumentReference<ITreatmentCategory>;
}

export interface ICreditUsedData {
  credit: WithRef<IAccountCredit>;
  invoice: WithRef<IInvoice>;
  transaction: WithRef<ITransaction>;
  usedAccountCredit: IUsedAccountCredit;
}
